import { store } from "../store";
import { openKjuupIoAbo } from "../pages/Cockpit/cockpitNavigation";

/** Company-billed Kjuup.io (Admin Panel) vs personal Stripe. */
export function isCompanyManagedKjuupIo(subscription) {
  if (!subscription) return false;
  return (
    !!subscription.isKjuupPlusMember || subscription.source === "company"
  );
}

/** True only after company trial fully ended (Stripe canceled) while user still has a seat. */
export function isCompanyTrialEnded(subscription) {
  return !!(subscription && subscription.companyTrialEnded && !subscription.active);
}

/** Company admin who still has an assigned Kjuup.io seat. */
export function isCompanyAdminWithSeat(subscription) {
  return !!(
    subscription &&
    subscription.isCompanyAdmin &&
    subscription.hasCompanySeat
  );
}

/**
 * 7 / 3 / 1 upgrade notes during an active company trial — company admins
 * with a seat only. Regular employees never see these.
 */
export function shouldShowCompanyTrialUpgradeNotes(subscription) {
  if (!subscription || !isCompanyManagedKjuupIo(subscription)) return false;
  if (isCompanyTrialEnded(subscription)) return false;
  if (!isCompanyAdminWithSeat(subscription)) return false;
  if (!subscription.active) return false;
  return !!(
    subscription.trialing ||
    subscription.isTrialing ||
    subscription.subscriptionStatus === "trialing"
  );
}

/**
 * Company trial-ended note: only for company admins who still have a seat
 * (subscription enabled via Admin Panel). Regular employees never see it.
 */
export function shouldShowCompanyTrialEndedNote(subscription) {
  if (!subscription || subscription.active) return false;
  if (subscription.showCompanyTrialEndedNote != null) {
    return !!subscription.showCompanyTrialEndedNote;
  }
  return (
    !!subscription.companyTrialEnded &&
    !!subscription.isCompanyAdmin &&
    (subscription.hasCompanySeat !== false)
  );
}

/** "First Last" joined for several admins. Prefers first name + last name. */
export function companyAdminNameList(admins) {
  const names = (admins || [])
    .map((a) => {
      if (!a) return "";
      const named = [a.firstName, a.lastName].filter(Boolean).join(" ").trim();
      return named || (a.name ? String(a.name).trim() : "");
    })
    .filter(Boolean);
  return names.join(", ");
}

/**
 * Kjuup+ member note: their Kjuup.io seat is booked by the named company admins.
 */
export function kjuupIoSeatContactNote(strings, admins) {
  const list = companyAdminNameList(admins);
  if (!list) {
    return (
      (strings && strings.kjuup_io_seat_company_admin_only) ||
      "The Kjuup.io package for your account can only be booked by your company admin. Contact your company admin for your Kjuup.io seat."
    );
  }
  const template =
    (strings && strings.kjuup_io_seat_company_admins) ||
    "The Kjuup.io package for your account can only be booked by your company admins {{admins}}. Contact {{admins}} for your Kjuup.io seat.";
  return template.replace(/\{\{admins\}\}/g, list);
}

/** "First Last" / "A & B" / "A, B & C" from companyAdmins payload. */
export function formatCompanyAdminNames(admins) {
  const names = (admins || [])
    .map((a) => {
      if (!a) return "";
      if (a.name) return String(a.name).trim();
      return [a.firstName, a.lastName].filter(Boolean).join(" ").trim();
    })
    .filter(Boolean);
  if (!names.length) return null;
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} & ${names[1]}`;
  return `${names.slice(0, -1).join(", ")} & ${names[names.length - 1]}`;
}

/** Body copy that names the company's admins (fallback if none returned). */
export function companyManagedAskBody(strings, subscription) {
  const names = formatCompanyAdminNames(subscription?.companyAdmins);
  if (names) {
    return (
      strings.kjuup_io_company_managed_body_short_admins ||
      "Ask {admins} to assign you a Kjuup.io seat in the Admin Panel."
    ).replace("{admins}", names);
  }
  return (
    strings.kjuup_io_company_managed_body_short ||
    "Ask your company admin to assign you a Kjuup.io seat in the Admin Panel."
  );
}

export function companyTrialEndedTitle(strings) {
  return (
    strings.kjuup_io_company_trial_ended_title ||
    strings.kjuup_io_trial_ended_title ||
    "Free trial ended!"
  );
}

export function companyTrialEndedAskBody(strings, subscription) {
  const names = formatCompanyAdminNames(subscription?.companyAdmins);
  if (names) {
    return (
      strings.kjuup_io_company_trial_ended_contact_admins ||
      "The Kjuup.io subscription can be assigned by {admins} via the Admin Center."
    ).replace("{admins}", names);
  }
  return (
    strings.kjuup_io_company_trial_ended_body ||
    "The Kjuup.io subscription can be assigned via the Admin Center."
  );
}

/**
 * Personal → /subscription.
 * Company admin with a seat → the Kjuup.io Abo page (plan, seats, billing).
 * Other company users → /subscription (admin-names note).
 */
export function navigateToKjuupIoBilling(history, subscription) {
  if (isCompanyAdminWithSeat(subscription)) {
    const companyId = subscription?.companyBilling?.companyId;
    if (companyId) {
      if (history) {
        openKjuupIoAbo(store.dispatch, history, companyId);
      } else {
        window.location.assign(`/kjuup-io/${companyId}`);
      }
      return true;
    }
  }
  if (history) {
    history.push("/subscription");
    return true;
  }
  return false;
}
